import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, ExternalLink, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card, Field, ImageUpload, BigButton, GhostButton } from "@/components/admin/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const TYPE_LABEL = { hero: "Top banner", story: "Story / intro", cards: "Journey cards", values: "Values", gallery: "Photo gallery", instagram: "Instagram photo strip",
  services: "Services list", courses: "Courses list", packages: "Packages", cta: "WhatsApp call-to-action", contact: "Contact block" };
const uid = () => Math.random().toString(16).slice(2, 14);
const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const a = [...arr];
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

const OffBadge = ({ on }) => !on && <span className="text-[11px] font-bold bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">Off</span>;

const OrderBtns = ({ onUp, onDown, testid }) => (
  <span className="flex">
    <button type="button" onClick={onUp} className="p-2 text-slate-500 hover:text-ink" aria-label="Move up" data-testid={`${testid}-up`}><ArrowUp className="w-4 h-4" /></button>
    <button type="button" onClick={onDown} className="p-2 text-slate-500 hover:text-ink" aria-label="Move down" data-testid={`${testid}-down`}><ArrowDown className="w-4 h-4" /></button>
  </span>
);

const ItemEditor = ({ it, idx, onChange, onToggle, onMove, onDelete, isGallery, isInsta }) => (
  <div className={`rounded-xl border border-border p-3 space-y-3 ${it.enabled ? "bg-white" : "bg-slate-50 opacity-60"}`} data-testid={`content-item-${it.id}`}>
    <div className="flex items-center gap-2">
      <Switch checked={it.enabled} onCheckedChange={onToggle} data-testid={`content-item-toggle-${it.id}`} />
      <p className="font-semibold flex-1 truncate">{it.title || (isGallery ? `Photo ${idx + 1}` : "Untitled")}</p>
      <OffBadge on={it.enabled} />
      <OrderBtns onUp={() => onMove(-1)} onDown={() => onMove(1)} testid={`content-item-${it.id}`} />
      <button type="button" onClick={onDelete} className="p-2 text-slate-400 hover:text-rose-600" aria-label="Delete item" data-testid={`content-item-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      {!isGallery && <Field label="Title"><Input value={it.title} onChange={(e) => onChange({ title: e.target.value })} data-testid={`content-item-title-${it.id}`} /></Field>}
      {!isGallery && <Field label="Subtitle"><Input value={it.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} data-testid={`content-item-subtitle-${it.id}`} /></Field>}
      {!isGallery && <Field label="Text" className="sm:col-span-2"><Textarea value={it.body} onChange={(e) => onChange({ body: e.target.value })} data-testid={`content-item-body-${it.id}`} /></Field>}
      <Field label="Photo"><ImageUpload value={it.image} onChange={(v) => onChange({ image: v })} compact testid={`content-item-image-${it.id}`} /></Field>
      {(!isGallery || isInsta) && <Field label={isInsta ? "Instagram post link (optional)" : "Link (optional)"}><Input value={it.link} onChange={(e) => onChange({ link: e.target.value })} placeholder={isInsta ? "https://www.instagram.com/p/..." : "/scuba-diving"} data-testid={`content-item-link-${it.id}`} /></Field>}
    </div>
  </div>
);

const SectionEditor = ({ s, onChange, onToggle, onMove, autosave }) => {
  const [open, setOpen] = useState(false);
  const setItems = (items, save) => (save ? autosave({ items }) : onChange({ items }));
  const isGallery = ["gallery", "instagram"].includes(s.type);
  return (
    <Card className={`${s.enabled ? "" : "bg-slate-50"}`} data-testid={`content-section-${s.id}`}>
      <div className="flex items-center gap-3 p-4">
        <Switch checked={s.enabled} onCheckedChange={onToggle} data-testid={`content-section-toggle-${s.id}`} />
        <button onClick={() => setOpen(!open)} className={`flex-1 min-w-0 text-left ${s.enabled ? "" : "opacity-60"}`} data-testid={`content-section-expand-${s.id}`}>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{TYPE_LABEL[s.type] || s.type}</p>
          <p className="font-extrabold truncate">{s.title || "—"}</p>
        </button>
        <OffBadge on={s.enabled} />
        <OrderBtns onUp={() => onMove(-1)} onDown={() => onMove(1)} testid={`content-section-${s.id}`} />
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="border-t border-border p-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            {["hero", "contact"].includes(s.type) && <Field label="Small label above title"><Input value={s.eyebrow} onChange={(e) => onChange({ eyebrow: e.target.value })} data-testid={`content-section-eyebrow-${s.id}`} /></Field>}
            <Field label="Title"><Input value={s.title} onChange={(e) => onChange({ title: e.target.value })} data-testid={`content-section-title-${s.id}`} /></Field>
            {!["cards", "values", "gallery", "instagram", "packages", "cta"].includes(s.type) && <Field label="Subtitle" className="sm:col-span-2"><Textarea value={s.subtitle} onChange={(e) => onChange({ subtitle: e.target.value })} data-testid={`content-section-subtitle-${s.id}`} /></Field>}
            {!["cards", "values", "gallery", "instagram", "packages", "services", "courses"].includes(s.type) && <Field label="Text" className="sm:col-span-2"><Textarea rows={4} value={s.body} onChange={(e) => onChange({ body: e.target.value })} data-testid={`content-section-body-${s.id}`} /></Field>}
            {!["cards", "values", "gallery", "instagram", "packages"].includes(s.type) && <Field label="Section photo"><ImageUpload value={s.image} onChange={(v) => onChange({ image: v })} testid={`content-section-image-${s.id}`} /></Field>}
          </div>
          {["cards", "values", "gallery", "instagram", "packages", "services", "courses"].includes(s.type) && (
            <div className="space-y-2">
              <p className="font-bold text-sm">{isGallery ? "Photos" : "Items"}</p>
              {s.items.map((it, i) => (
                <ItemEditor key={it.id} it={it} idx={i} isGallery={isGallery} isInsta={s.type === "instagram"}
                  onChange={(patch) => setItems(s.items.map((x) => (x.id === it.id ? { ...x, ...patch } : x)))}
                  onToggle={(v) => setItems(s.items.map((x) => (x.id === it.id ? { ...x, enabled: v } : x)), true)}
                  onMove={(d) => setItems(move(s.items, i, d))}
                  onDelete={() => setItems(s.items.filter((x) => x.id !== it.id))} />
              ))}
              <GhostButton className="!h-10" onClick={() => setItems([...s.items, { id: uid(), enabled: true, title: "", subtitle: "", body: "", image: "", link: "" }])} data-testid={`content-add-item-${s.id}`}>
                <Plus className="w-4 h-4" />{isGallery ? "Add photo" : "Add item"}
              </GhostButton>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default function ContentEditor() {
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState("home");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { api.get("/admin/pages").then((r) => setPages(r.data)); }, []);
  const page = pages.find((p) => p.slug === slug);

  const persist = async (p) => {
    try {
      const r = await api.put(`/admin/pages/${p.slug}`, p);
      setPages((ps) => ps.map((x) => (x.slug === p.slug ? r.data : x)));
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const local = (p) => { setPages((ps) => ps.map((x) => (x.slug === p.slug ? p : x))); setDirty(true); };
  const toggle = async (p) => { if (await persist(p)) { setDirty(false); toast.success("Saved — live on the website now"); } };
  const saveAll = async () => { if (await persist(page)) { setDirty(false); toast.success("Changes published"); } };
  const updSection = (id, patch) => ({ ...page, sections: page.sections.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  return (
    <div className="max-w-4xl pb-20" data-testid="content-editor-page">
      <PageHeader title="Website content" subtitle="Switch anything Off to hide it — the text and photos stay saved here."
        actions={<a href={slug === "home" ? "/" : `/${slug}`} target="_blank" rel="noreferrer"><GhostButton data-testid="content-preview-btn"><ExternalLink className="w-4 h-4" />View page</GhostButton></a>} />
      <div className="grid sm:grid-cols-4 gap-2 mb-5">
        {pages.map((p) => (
          <Card key={p.slug} className={`p-3 flex items-center gap-2 cursor-pointer ${slug === p.slug ? "border-ocean ring-1 ring-ocean" : ""} ${p.enabled ? "" : "bg-slate-50"}`} data-testid={`content-page-${p.slug}`}>
            <button className={`flex-1 text-left font-bold ${p.enabled ? "" : "opacity-60"}`} onClick={() => setSlug(p.slug)} data-testid={`content-page-select-${p.slug}`}>{p.nav_label}</button>
            <OffBadge on={p.enabled} />
            <Switch checked={p.enabled} disabled={p.slug === "home"} onCheckedChange={(v) => toggle({ ...p, enabled: v })} data-testid={`content-page-toggle-${p.slug}`} />
          </Card>
        ))}
      </div>
      {page && (
        <div className="space-y-3">
          <Card className="p-4"><Field label="Menu label"><Input value={page.nav_label} onChange={(e) => local({ ...page, nav_label: e.target.value })} className="h-12" data-testid="content-nav-label-input" /></Field></Card>
          {page.sections.map((s, i) => (
            <SectionEditor key={s.id} s={s}
              onChange={(patch) => local(updSection(s.id, patch))}
              autosave={(patch) => toggle(updSection(s.id, patch))}
              onToggle={(v) => toggle(updSection(s.id, { enabled: v }))}
              onMove={(d) => local({ ...page, sections: move(page.sections, i, d) })} />
          ))}
        </div>
      )}
      {dirty && (
        <div className="fixed bottom-20 lg:bottom-6 inset-x-4 lg:left-auto lg:right-10 lg:w-96 z-30">
          <BigButton onClick={saveAll} className="w-full shadow-xl" data-testid="content-save-btn">Publish changes</BigButton>
        </div>
      )}
    </div>
  );
}
